import { useState, useCallback } from "react";
import apiClient from "utils/apiClient";
import { PM_SERVICE_BASE_URL, ACTOR_ID_HEADER_KEY, O_CLIENT_APP, O_API_KEY } from "constants";
import { useAuth } from "contexts/AuthContext";
import useFilters from "hooks/useFilters";

const useTasks = (projectId = null) => {
  const { user } = useAuth();

  const [state, setState] = useState({
    tasks: [],
    tasksByIteration: {},
    tasksByProject: {},
    loading: false,
    error: null,
  });

  const buildHeaders = useCallback(() => {
    const headers = {
      "O-CLIENT-APP": O_CLIENT_APP,
      "O-API-KEY": O_API_KEY,
    };
    if (user?.id) {
      headers["X-User-Id"] = user.id;
    }
    return headers;
  }, [user?.id]);

  const { selectedTagIds } = useFilters();

  /** Generic GET helper **/
  const getRequest = useCallback(async (url) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.response?.data?.error || err.message || "An unexpected error occurred";
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic POST helper **/
  const postRequest = useCallback(async (url, payload, config = {}) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.post(url, payload, config);
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.response?.data?.error || err.message || "An unexpected error occurred";
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic PUT helper **/
  const putRequest = useCallback(async (url, payload, config = {}) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.put(url, payload, config);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic DELETE helper **/
  const deleteRequest = useCallback(async (url, config = {}) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await apiClient.delete(url, config);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Fetch tasks for a specific iteration **/
  const fetchTasksByIteration = useCallback(
    async (projectIdToFetch, iterationIdToFetch) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectIdToFetch}/tasks?iterationId=${iterationIdToFetch}&size=1000`;
        const data = await getRequest(url);
        const tasksList = data.content ? data.content : Array.isArray(data) ? data : [data];

        setState((prev) => ({
          ...prev,
          tasksByIteration: {
            ...prev.tasksByIteration,
            [iterationIdToFetch]: tasksList,
          },
          loading: false,
        }));

        return tasksList;
      } catch (err) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err.message || "Failed to fetch tasks",
        }));
        throw err;
      }
    },
    [getRequest]
  );

  /** Fetch tasks for all iterations in a project **/
  const fetchAllTasksForIterations = useCallback(async (projectIdToFetch, iterationIds) => {
    if (!iterationIds || iterationIds.length === 0) {
      return {};
    }

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const promises = iterationIds.map((iterId) =>
        apiClient
          .get(`${PM_SERVICE_BASE_URL}/api/v1/projects/${projectIdToFetch}/tasks?iterationId=${iterId}&size=1000`)
          .then((res) => {
            const data = res.data;
            const tasks = data.content ? data.content : Array.isArray(data) ? data : [];
            return { iterationId: iterId, tasks };
          })
          .catch((err) => {
            console.error(`[useTasks] Failed to fetch tasks for iteration ${iterId}:`, err);
            return { iterationId: iterId, tasks: [] };
          })
      );

      const results = await Promise.all(promises);

      const tasksMap = {};
      results.forEach(({ iterationId, tasks }) => {
        tasksMap[iterationId] = tasks;
      });

      setState((prev) => ({
        ...prev,
        tasksByIteration: { ...prev.tasksByIteration, ...tasksMap },
        loading: false,
      }));

      return tasksMap;
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Failed to fetch tasks",
      }));
      throw err;
    }
  }, []);

  /** Fetch tasks for a project **/
  const fetchTasksByProject = useCallback(
    async (projectIdToFetch) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectIdToFetch}/tasks?size=1000`;
        const data = await getRequest(url);
        const tasksList = data.content ? data.content : Array.isArray(data) ? data : [data];

        setState((prev) => ({
          ...prev,
          tasksByProject: {
            ...prev.tasksByProject,
            [projectIdToFetch]: tasksList,
          },
          loading: false,
        }));

        return tasksList;
      } catch (err) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err.message || "Failed to fetch tasks",
        }));
        throw err;
      }
    },
    [getRequest]
  );

  /** Create task **/
  const createTask = useCallback(
    async (projectId, taskData) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks`;
        const headers = {};
        if (user?.id) {
          headers[ACTOR_ID_HEADER_KEY] = user.id;
        }
        const response = await apiClient.post(url, taskData, {
          ...(Object.keys(headers).length > 0 && { headers }),
        });
        const data = response.data;

        if (data.iterationId) {
          setState((prev) => ({
            ...prev,
            tasksByIteration: {
              ...prev.tasksByIteration,
              [data.iterationId]: [...(prev.tasksByIteration[data.iterationId] || []), data],
            },
          }));
        }

        setState((prev) => ({
          ...prev,
          tasksByProject: {
            ...prev.tasksByProject,
            [projectId]: [...(prev.tasksByProject[projectId] || []), data],
          },
          loading: false,
        }));

        return data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
        throw new Error(errorMessage);
      }
    },
    [user?.id]
  );

  /** Update task **/
  const updateTask = useCallback(
    async (projectId, taskId, taskData) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}`;
        const headers = {};
        if (user?.id) {
          headers[ACTOR_ID_HEADER_KEY] = user.id;
        }
        const response = await apiClient.put(url, taskData, {
          ...(Object.keys(headers).length > 0 && { headers }),
        });
        const data = response.data;

        if (data.iterationId) {
          setState((prev) => ({
            ...prev,
            tasksByIteration: {
              ...prev.tasksByIteration,
              [data.iterationId]: (prev.tasksByIteration[data.iterationId] || []).map((task) =>
                task.id === taskId ? data : task
              ),
            },
          }));
        }

        setState((prev) => ({
          ...prev,
          tasksByProject: {
            ...prev.tasksByProject,
            [projectId]: (prev.tasksByProject[projectId] || []).map((task) =>
              task.id === taskId ? data : task
            ),
          },
          loading: false,
        }));

        return data;
      } catch (err) {
        throw err;
      }
    },
    [user?.id]
  );

  /** Delete task **/
  const deleteTask = useCallback(
    async (projectId, taskId) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}`;
        await deleteRequest(url);

        setState((prev) => {
          const newTasksByIteration = { ...prev.tasksByIteration };
          Object.keys(newTasksByIteration).forEach((iterId) => {
            newTasksByIteration[iterId] = newTasksByIteration[iterId].filter((task) => task.id !== taskId);
          });
          return { ...prev, tasksByIteration: newTasksByIteration };
        });

        setState((prev) => ({
          ...prev,
          tasksByProject: {
            ...prev.tasksByProject,
            [projectId]: (prev.tasksByProject[projectId] || []).filter((task) => task.id !== taskId),
          },
          loading: false,
        }));
      } catch (err) {
        throw err;
      }
    },
    [deleteRequest]
  );

  // ==================== TASK COMMENTS ====================

  /** Fetch comments for a task **/
  /** GET: /api/v1/projects/{projectId}/tasks/{taskId}/comments **/
  const fetchTaskComments = useCallback(async (projectId, taskId) => {
    try {
      const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/comments?sort=createdAt,desc&page=0`;
      const data = await getRequest(url);
      const comments = data.content ? data.content : (Array.isArray(data) ? data : []);
      setState((prev) => ({ ...prev, loading: false }));
      return comments;
    } catch (err) {
      console.error("[useTasks:fetchTaskComments]", err);
      setState((prev) => ({ ...prev, loading: false }));
      throw err;
    }
  }, [getRequest]);

  /** Add a comment to a task **/
  const addTaskComment = useCallback(
    async (projectId, taskId, commentData) => {
      const { id, email, firstName, lastName, username } = user || {};

      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/comments`;
        const headers = {};
        if (id) {
          headers["X-User-Id"] = id;
          const userName = `${firstName || ""} ${lastName || ""}`.trim() || email || username || String(id);
          headers["X-User-Name"] = userName;
        }

        const data = await postRequest(url, commentData, { headers });
        setState((prev) => ({ ...prev, loading: false }));
        return data;
      } catch (err) {
        throw err;
      }
    },
    [postRequest, user?.id, user?.email, user?.firstName, user?.lastName, user?.username]
  );

  /** Update a comment **/
  const updateTaskComment = useCallback(
    async (projectId, taskId, commentId, commentData) => {
      const { id, email, firstName, lastName, username } = user || {};

      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/comments/${commentId}`;
        const headers = {};
        if (id) {
          headers["X-User-Id"] = id;
          const userName = `${firstName || ""} ${lastName || ""}`.trim() || email || username || String(id);
          headers["X-User-Name"] = userName;
        }

        const data = await putRequest(url, commentData, { headers });
        setState((prev) => ({ ...prev, loading: false }));
        return data;
      } catch (err) {
        throw err;
      }
    },
    [putRequest, user?.id, user?.email, user?.firstName, user?.lastName, user?.username]
  );

  /** Delete a comment **/
  const deleteTaskComment = useCallback(
    async (projectId, taskId, commentId) => {
      const { id, email, firstName, lastName, username } = user || {};

      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/comments/${commentId}`;
        const headers = {};
        if (id) {
          headers["X-User-Id"] = id;
          const userName = `${firstName || ""} ${lastName || ""}`.trim() || email || username || String(id);
          headers["X-User-Name"] = userName;
        }

        await deleteRequest(url, { headers });
        setState((prev) => ({ ...prev, loading: false }));
      } catch (err) {
        throw err;
      }
    },
    [deleteRequest, user?.id, user?.email, user?.firstName, user?.lastName, user?.username]
  );

  // ==================== TASK LINKS ====================

  /** Fetch links for a task **/
  const fetchTaskLinks = useCallback(
    async (taskId) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/links/tasks/${taskId}/links`;
        const response = await apiClient.get(url, {
          headers: buildHeaders(),
        });

        if (Array.isArray(response.data)) {
          return response.data;
        }

        if (Array.isArray(response.data?.data)) {
          return response.data.data;
        }

        console.warn("[fetchTaskLinks] Unexpected response shape:", response.data);
        return [];
      } catch (err) {
        throw err;
      }
    },
    [buildHeaders]
  );

  /** Create link(s) **/
  const addTaskLink = useCallback(
    async (taskId, link) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/links/tasks/${taskId}/links`;

        const payload = [
          {
            url: link.url,
            label: link.label ?? null,
          },
        ];
        const response = await apiClient.post(url, payload, {
          headers: buildHeaders(),
        });

        return response.data;
      } catch (err) {
        throw err;
      }
    },
    [buildHeaders]
  );

  /** Update a single link **/
  const updateTaskLink = useCallback(
    async (taskId, linkId, link) => {
      const headers = {};
      if (user?.id) {
        headers["X-User-Id"] = user.id;
      }

      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/links/tasks/${taskId}/links/${linkId}`;

        const payload = {
          url: link.url,
          label: link.label ?? null,
        };

        const response = await apiClient.put(url, payload, { headers });
        return response.data;
      } catch (err) {
        throw err;
      }
    },
    [user?.id]
  );

  /** Delete a single link **/
  const deleteTaskLink = useCallback(
    async (taskId, linkId) => {
      const headers = {};
      if (user?.id) {
        headers["X-User-Id"] = user.id;
      }

      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/links/tasks/${taskId}/links/${linkId}`;
        await apiClient.delete(url, { headers });
      } catch (err) {
        throw err;
      }
    },
    [user?.id]
  );

  // Helper functions for filtering

  function ReactifyFilter(items = [], selectedTagIds = []) {
    if (!selectedTagIds || selectedTagIds.length === 0) return items;
    const setIds = new Set(selectedTagIds);
    return items.filter((it) => {
      const tags = it.tags || [];
      const tagIds = tags
        .map((t) => (typeof t === "string" ? t : t.id))
        .filter(Boolean);
      return tagIds.some((id) => setIds.has(id));
    });
  }

  function mapFilterTasks(map = {}, selectedTagIds = []) {
    if (!selectedTagIds || selectedTagIds.length === 0) return map;
    const out = {};
    Object.keys(map).forEach((k) => {
      out[k] = ReactifyFilter(map[k] || [], selectedTagIds);
    });
    return out;
  }

  return {
    tasks: ReactifyFilter(projectId ? state.tasksByProject[projectId] || [] : [], selectedTagIds),
    tasksByIteration: mapFilterTasks(state.tasksByIteration, selectedTagIds),
    tasksByProject: mapFilterTasks(state.tasksByProject, selectedTagIds),
    loading: state.loading,
    error: state.error,
    fetchTasksByIteration,
    fetchAllTasksForIterations,
    fetchTasksByProject,
    createTask,
    updateTask,
    deleteTask,
    // Comments
    fetchTaskComments,
    addTaskComment,
    updateTaskComment,
    deleteTaskComment,
    // Links
    fetchTaskLinks,
    addTaskLink,
    updateTaskLink,
    deleteTaskLink,
  };
};

export default useTasks;
